import { useAuth } from "../../contexts/AuthContext";
import { Avatar } from "../../design/avatar";
import { Dropdown, DropdownButton } from "../../design/dropdown";
import {
  Navbar,
  NavbarItem,
  NavbarSection,
  NavbarSpacer,
} from "../../design/navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarItem,
  SidebarSection,
} from "../../design/sidebar";
import { SidebarLayout } from "../../design/collaboration-layout";
import { Bars3Icon, ChevronUpIcon } from "@heroicons/react/20/solid";
import { Badge } from "../../design/badge";
import ChatBubble from "../ChatBubble/ChatBubble";
import { useMatter } from "../../contexts/MatterContext";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import SideBarView from "../SideBarView";
import LogoHorizontal from "../LogoHorizontal";
import { useTask } from "../../contexts/TaskContext";
import { useTenant } from "../../contexts/TenantContext";
import { Switch, SwitchField } from "../../design/switch";
import { useSetting } from "../../contexts/SettingContextProvider";
import AdminSideBarView from "../AdminSideBarView";
import { Subheading } from "../../design/heading";
import UserSidebar from "./components/UserSidebar";
import AdminSidebar from "./components/AdminSidebar";
import { useInvitation } from "../../contexts/InvitationContext";
import { AccountDropdownMenu } from "./components/AccountDropdownMenu";
import { Text } from "../../design/text";

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const pathSegments = location.pathname.split("/");

  const entityType = pathSegments[1];
  const id = pathSegments[2];

  const { tenant } = useTenant();
  const { user } = useAuth();
  const { currentUser } = useUser();
  const { isAdmin, setIsAdmin } = useSetting();

  useEffect(() => {
    document.title = tenant?.name ?? "CaseTime";
  }, [tenant]);

  useEffect(() => {
    if (!user) return;
    if (!currentUser) return;
  }, [user, currentUser]);

  return (
    <SidebarLayout
      navbar={<Navbar></Navbar>}
      sidebar={
        <Sidebar>
          <div>
            <LogoHorizontal tenant={tenant} className="h-6 mt-6 mb-6 ml-6" />
          </div>

          <SidebarBody>
            {currentUser &&
              ["superadmin", "admin"].includes(currentUser.role) && (
                <SidebarSection>
                  <SwitchField>
                    <Subheading>Admin</Subheading>
                    <Switch
                      checked={isAdmin}
                      onChange={(checked) => setIsAdmin(checked)}
                    />
                  </SwitchField>
                </SidebarSection>
              )}
            {/* <ProfessionalSidebar /> */}
            {!isAdmin && <UserSidebar />}
            {isAdmin && <AdminSidebar />}
          </SidebarBody>

          <SidebarFooter>
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <div className="flex items-center gap-2">
                  <Avatar
                    src={user?.photoURL ?? undefined}
                    initials={`${currentUser?.firstName?.charAt(0)} ${currentUser?.lastName?.charAt(0)}`}
                    className="size-10"
                  />
                  <div>
                    <div className="flex items-center gap-2">
                      <Subheading>{currentUser?.name}</Subheading>
                      <Badge>1</Badge>
                    </div>
                    <Text>{currentUser?.email ?? user?.email}</Text>
                  </div>
                </div>
                <ChevronUpIcon />
              </DropdownButton>
              <AccountDropdownMenu anchor="top start" />
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
      rightSidebar={
        (entityType === "discovery-request" && (
          <SideBarView discoveryRequestId={id!} />
        )) ||
        (entityType === "task" && <SideBarView taskId={id!} />) ||
        (entityType === "subtask" && <SideBarView subtaskId={id!} />) ||
        (entityType === "participant" && <SideBarView participantId={id!} />) ||
        (entityType === "event" && <SideBarView eventId={id!} />) ||
        (entityType === "document" && <SideBarView documentId={id!} />) ||
        (entityType === "file" && <SideBarView fileId={id!} />) ||
        (entityType === "child-support-order" && (
          <SideBarView childSupportOrderId={id!} />
        )) ||
        (entityType === "child-support-payment" && (
          <SideBarView childSupportPaymentId={id!} />
        )) ||
        (entityType === "client" && isAdmin && (
          <AdminSideBarView clientId={id!} />
        )) ||
        (entityType === "intake" && isAdmin && (
          <AdminSideBarView intakeId={id!} />
        )) ||
        (entityType === "matter" && isAdmin && (
          <AdminSideBarView matterId={id!} />
        )) ||
        (entityType === "matterType" && isAdmin && (
          <AdminSideBarView matterTypeId={id!} />
        )) ||
        null
      }
    >
      {children}
      <ChatBubble />
    </SidebarLayout>
  );
};

export default Layout;
